import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DollarSign, Calendar, PackageX, PackageOpen, Sparkles, Send } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { InventoryPolicyControlScopeSelector } from '../components/InventoryPolicyControlScopeSelector';
import { inventoryPolicyControlDashboardMocks } from '../data/controlCenter.mock';
import { useInventoryPolicyControlScope } from '../hooks/useInventoryPolicyControlScope';
import { useCurrency } from '@/hooks/useCurrency';
import type { ControlPriority } from '../model/controlCenter.types';

const PIE_COLORS: Record<number, string> = {
  1: '#EF4444',
  2: '#F59E0B',
  3: '#22C55E',
  4: '#EAB308',
  5: '#DC2626',
};

function priorityBadgeClass(p: ControlPriority): string {
  switch (p) {
    case 'alta':
      return 'bg-red-100 text-red-800';
    case 'media':
      return 'bg-amber-100 text-amber-800';
    case 'baja':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

interface CriticalDimensionRow {
  name: string;
  affectedPolicies: number;
  impact: number;
}

function normalizeLabel(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function inferFamilyFromProductName(productName: string): string {
  const name = normalizeLabel(productName);

  if (/freno|disco|pastilla/.test(name)) return 'Frenos';
  if (/cable|cargador|adaptador|protector|funda|hub|powerbank/.test(name)) return 'Accesorios moviles';
  if (/auricular|altavoz|audio|noise cancel/.test(name)) return 'Audio portatil';
  if (/gaming|teclado|mouse|raton/.test(name)) return 'Accesorios gaming';
  if (/watch|smartwatch|pulsera/.test(name)) return 'Wearables';
  if (/aluminio|acero|pletina|placa|escuadra|perfil/.test(name)) return 'Metal y perfileria';
  if (/componente|conector|terminal|tornillo|abrazadera/.test(name)) return 'Componentes';
  if (/llave|herramienta/.test(name)) return 'Herramientas';

  return 'Otras familias';
}

function projectRowsToTotal(
  rows: Array<{ name: string; sampleCount: number }>,
  totalPolicies: number,
  totalImpact: number
): CriticalDimensionRow[] {
  if (rows.length === 0 || totalPolicies <= 0) return [];

  const sampleTotal = rows.reduce((sum, row) => sum + row.sampleCount, 0);
  if (sampleTotal === 0) return [];

  const projected = rows.map((row) => {
    const rawPolicies = (row.sampleCount / sampleTotal) * totalPolicies;
    const rawImpact = (row.sampleCount / sampleTotal) * totalImpact;
    return {
      name: row.name,
      sampleCount: row.sampleCount,
      affectedPolicies: Math.floor(rawPolicies),
      impact: Math.round(rawImpact),
      remainder: rawPolicies - Math.floor(rawPolicies),
    };
  });

  const assignedPolicies = projected.reduce((sum, row) => sum + row.affectedPolicies, 0);
  const missingPolicies = Math.max(0, totalPolicies - assignedPolicies);

  projected
    .sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      return b.sampleCount - a.sampleCount;
    })
    .slice(0, missingPolicies)
    .forEach((row) => {
      row.affectedPolicies += 1;
    });

  return projected
    .sort((a, b) => {
      if (b.affectedPolicies !== a.affectedPolicies) return b.affectedPolicies - a.affectedPolicies;
      return b.impact - a.impact;
    })
    .slice(0, 10)
    .map(({ name, affectedPolicies, impact }) => ({ name, affectedPolicies, impact }));
}

export function InventoryPolicyControlPage() {
  const { t } = useTranslation();
  const { formatRounded } = useCurrency();
  const { scope, setScope, showScopeSelector, buildScopedPath } = useInventoryPolicyControlScope();
  const data = inventoryPolicyControlDashboardMocks[scope];
  const [aiQuery, setAiQuery] = useState('');

  const pieData = useMemo(
    () =>
      data.health.segments.map((s) => ({
        name: s.statusLabel,
        value: s.percentage,
        statusOrder: s.statusOrder,
      })),
    [data.health.segments]
  );

  const criticalTables = useMemo(() => {
    const totalPolicies = data.kpi.policiesToReviewCount;
    const totalImpactPool = data.topProblems.reduce((sum, problem) => sum + problem.economicImpact, 0);

    const sampledPolicies = Array.from(
      data.problemInsights
        .flatMap((problem) => problem.affectedPolicies)
        .reduce<Map<string, (typeof data.problemInsights)[number]['affectedPolicies'][number]>>((acc, policy) => {
          const key = `${policy.productCode}::${policy.pointOfSale}::${policy.supplier}`;
          if (!acc.has(key)) {
            acc.set(key, policy);
          }
          return acc;
        }, new Map())
        .values()
    );

    const aggregateBy = (getLabel: (policy: (typeof sampledPolicies)[number]) => string): CriticalDimensionRow[] => {
      const counts = sampledPolicies.reduce<Record<string, number>>((acc, policy) => {
        const label = getLabel(policy);
        acc[label] = (acc[label] ?? 0) + 1;
        return acc;
      }, {});

      return projectRowsToTotal(
        Object.entries(counts).map(([name, sampleCount]) => ({ name, sampleCount })),
        totalPolicies,
        totalImpactPool
      );
    };

    return {
      primary: aggregateBy((policy) => (scope === 'stores' ? policy.pointOfSale : policy.supplier)),
      family: aggregateBy((policy) => inferFamilyFromProductName(policy.productName)),
    };
  }, [data.kpi.policiesToReviewCount, data.problemInsights, data.topProblems, scope]);

  return (
    <div className="p-6 pb-16 space-y-6 min-h-full">
      <div className="flex flex-col gap-3">
        {showScopeSelector ? (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700">{t('inventoryPolicyControl.scope.label')}</span>
            <InventoryPolicyControlScopeSelector scope={scope} onChange={setScope} />
          </div>
        ) : null}

        <div className="flex w-full flex-col gap-2 sm:flex-row">
          <input
            type="search"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            placeholder={t('inventoryPolicyControl.ai.placeholder')}
            className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#1681ba] focus:outline-none focus:ring-2 focus:ring-[#1681ba]/30"
            aria-label={t('inventoryPolicyControl.ai.label')}
          />
          <button
            type="button"
            className="inline-flex shrink-0 items-center justify-center rounded-lg bg-[#1681ba] px-4 py-3 text-white shadow-sm hover:bg-[#1472a6] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1681ba] disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t('inventoryPolicyControl.ai.send')}
            disabled
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-[#1681ba]" aria-hidden />
            <h3 className="text-base font-semibold text-gray-900">{t('inventoryPolicyControl.kpi.inventoryValue')}</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatRounded(data.kpi.totalInventoryValueEur)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-[#1681ba]" aria-hidden />
            <h3 className="text-base font-semibold text-gray-900">{t('inventoryPolicyControl.kpi.monthsOfInventory')}</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.kpi.monthsOfInventory.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <PackageX className="h-4 w-4 text-[#1681ba]" aria-hidden />
            <h3 className="text-base font-semibold text-gray-900">{t('inventoryPolicyControl.kpi.skusNoStock')}</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.kpi.skusNoStockCount.toLocaleString('es-ES')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <PackageOpen className="h-4 w-4 text-[#1681ba]" aria-hidden />
            <h3 className="text-base font-semibold text-gray-900">{t('inventoryPolicyControl.kpi.skusOverstock')}</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.kpi.skusOverstockCount.toLocaleString('es-ES')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200 flex flex-col items-center justify-center text-center sm:col-span-2 xl:col-span-1">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-[#1681ba]" aria-hidden />
            <h3 className="text-base font-semibold text-gray-900">{t('inventoryPolicyControl.kpi.policiesToReview')}</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.kpi.policiesToReviewCount.toLocaleString('es-ES')}</p>
          <p className="text-sm text-green-600 mt-1 flex items-center justify-center gap-1">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {t('inventoryPolicyControl.kpi.detectedByAi')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[0.92fr_1.08fr] gap-5">
        <section className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('inventoryPolicyControl.health.title')}</h2>
          <div className="flex justify-center">
            <div className="flex flex-col md:flex-row items-center md:items-center gap-3 md:gap-5 mx-auto">
              <div className="relative w-full max-w-[300px] h-[300px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={84}
                      outerRadius={122}
                      paddingAngle={1}
                    >
                      {pieData.map((entry) => (
                        <Cell key={`cell-${entry.name}`} fill={PIE_COLORS[entry.statusOrder] ?? '#6B7280'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [`${v}%`, t('inventoryPolicyControl.health.share')]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="text-center px-2">
                    <p className="text-4xl font-bold text-gray-900">{data.health.overallScore}</p>
                    <p className="text-xs text-gray-500 mt-1 leading-tight">
                      {t('inventoryPolicyControl.health.overallLine', {
                        label: t(`inventoryPolicyControl.health.overallLabels.${data.health.overallLabelKey}`),
                      })}
                    </p>
                  </div>
                </div>
              </div>
              <ul className="w-full md:w-auto space-y-1.5 text-sm pt-1 md:min-w-[170px]">
                {data.health.segments.map((seg) => (
                  <li key={seg.statusKey} className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: PIE_COLORS[seg.statusOrder] ?? '#6B7280' }}
                      aria-hidden
                    />
                    <span className="text-gray-900 font-medium">{seg.statusLabel}</span>
                    <span className="text-gray-500">({seg.percentage}%)</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">{t('inventoryPolicyControl.health.footerHint')}</p>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 flex flex-col">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('inventoryPolicyControl.topProblems.title')}</h2>
          <ol className="space-y-2 flex-1">
            {data.topProblems.map((problem) => (
              <li
                key={problem.id}
                className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:gap-3 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate xl:min-w-0">{problem.problemLabel}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 xl:shrink-0">
                      <span>
                        {problem.affectedPoliciesCount.toLocaleString('es-ES')} {t('inventoryPolicyControl.topProblems.affectedPolicies')}
                      </span>
                      <span>{problem.impactLabel}</span>
                    </div>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${priorityBadgeClass(problem.priority)}`}>
                  {t(`inventoryPolicyControl.priority.${problem.priority}`)}
                </span>
              </li>
            ))}
          </ol>
          <Link
            to={buildScopedPath('/inventory-policy-control/problems')}
            className="mt-3 text-sm font-medium text-[#1681ba] hover:text-[#1472a6] text-left"
          >
            {t('inventoryPolicyControl.topProblems.viewAll')}
          </Link>
        </section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {scope === 'stores'
                ? t('inventoryPolicyControl.dimensions.criticalStoresTitle')
                : t('inventoryPolicyControl.dimensions.criticalSuppliersTitle')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{t('inventoryPolicyControl.dimensions.top10Hint')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">
                    {scope === 'stores'
                      ? t('inventoryPolicyControl.dimensions.colStore')
                      : t('inventoryPolicyControl.dimensions.colSupplier')}
                  </th>
                  <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.dimensions.colAffectedPolicies')}</th>
                  <th className="py-2 font-medium">{t('inventoryPolicyControl.dimensions.colImpact')}</th>
                </tr>
              </thead>
              <tbody>
                {criticalTables.primary.map((row) => (
                  <tr key={row.name} className="border-b border-gray-100 last:border-0">
                    <td className="py-3 pr-4 text-gray-900 font-medium">{row.name}</td>
                    <td className="py-3 pr-4 text-gray-900">
                      {row.affectedPolicies.toLocaleString('es-ES')} {t('inventoryPolicyControl.dimensions.affectedPolicies')}
                    </td>
                    <td className="py-3 text-gray-900">{formatRounded(row.impact)}</td>
                  </tr>
                ))}
                {criticalTables.primary.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-4 text-sm text-gray-500">
                      {t('inventoryPolicyControl.dimensions.noData')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('inventoryPolicyControl.dimensions.criticalFamiliesTitle')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{t('inventoryPolicyControl.dimensions.top10Hint')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.dimensions.colFamily')}</th>
                  <th className="py-2 pr-4 font-medium">{t('inventoryPolicyControl.dimensions.colAffectedPolicies')}</th>
                  <th className="py-2 font-medium">{t('inventoryPolicyControl.dimensions.colImpact')}</th>
                </tr>
              </thead>
              <tbody>
                {criticalTables.family.map((row) => (
                  <tr key={row.name} className="border-b border-gray-100 last:border-0">
                    <td className="py-3 pr-4 text-gray-900 font-medium">{row.name}</td>
                    <td className="py-3 pr-4 text-gray-900">
                      {row.affectedPolicies.toLocaleString('es-ES')} {t('inventoryPolicyControl.dimensions.affectedPolicies')}
                    </td>
                    <td className="py-3 text-gray-900">{formatRounded(row.impact)}</td>
                  </tr>
                ))}
                {criticalTables.family.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-4 text-sm text-gray-500">
                      {t('inventoryPolicyControl.dimensions.noData')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <div className="h-12" aria-hidden />
    </div>
  );
}
